
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';
import { Order, Product } from '../../types';
import { BarChart3, TrendingUp, ShoppingBag, DollarSign, ArrowUpRight, ArrowDownRight, Users, RotateCcw, Calendar, Search } from 'lucide-react';

interface ProductStat {
  name: string;
  image: string;
  sold: number;
  returned: number;
  revenue: number;
}

const AdminReports: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ordersRef = ref(db, 'orders');
    onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setOrders(Object.values(data));
      }
      setLoading(false);
    });
  }, []);

  const validOrders = orders.filter(o => o && o.status !== 'cancelled');
  const totalRevenue = validOrders.reduce((acc, o) => acc + (o.total || 0), 0);
  const totalOrders = orders.length;
  const completedOrders = orders.filter(o => o.status === 'delivered').length;
  const cancelledOrders = orders.filter(o => o.status === 'cancelled').length;
  const avgOrderValue = totalOrders > 0 ? totalRevenue / (totalOrders - cancelledOrders || 1) : 0;
  const cancellationRate = totalOrders > 0 ? (cancelledOrders / totalOrders) * 100 : 0;

  const productStats = orders.reduce((acc, order) => {
    // items dizisinin varlığı kontrol ediliyor (crash koruması)
    const items = order.items || [];
    items.forEach(item => {
      if (!item.product) return;
      const pId = item.product.id;
      if (!acc[pId]) {
        acc[pId] = { 
          name: item.product.name || 'Bilinmeyen Ürün', 
          image: (item.product.images && item.product.images[0]) || '', 
          sold: 0, 
          returned: 0, 
          revenue: 0 
        };
      }
      if (order.status !== 'cancelled') {
        acc[pId].sold += (item.quantity || 0);
        acc[pId].revenue += (item.product.price || 0) * (item.quantity || 0);
      } else {
        acc[pId].returned += (item.quantity || 0);
      }
    });
    return acc;
  }, {} as Record<string, ProductStat>);

  const statsArray = Object.values(productStats) as ProductStat[];
  const bestSellers = [...statsArray].sort((a, b) => b.sold - a.sold).slice(0, 5);
  const mostReturned = statsArray.filter(p => p.returned > 0).sort((a, b) => b.returned - a.returned).slice(0, 5);

  const getMonthlySales = () => {
    const months: Record<string, number> = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = d.toLocaleString('tr-TR', { month: 'long' });
      months[key] = 0;
    }

    validOrders.forEach(o => {
      if (!o.createdAt) return;
      const d = new Date(o.createdAt);
      const key = d.toLocaleString('tr-TR', { month: 'long' });
      if (months[key] !== undefined) {
        months[key] += (o.total || 0);
      }
    });

    return months;
  };

  const monthlySales = getMonthlySales();
  const maxMonthly = Math.max(...Object.values(monthlySales), 1);

  if (loading) return <div className="p-10 text-center animate-pulse text-stone-400">Raporlar hazırlanıyor...</div>;

  return (
    <div className="p-8 space-y-10 h-full overflow-y-auto scrollbar-hide">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-stone-900 flex items-center">
            <BarChart3 className="mr-3 text-amber-600" size={32} /> Raporlar & Analiz
          </h1>
          <p className="text-stone-500 font-medium italic mt-1">İşletmenizin performans verileri</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Brüt Ciro', value: `₺${totalRevenue.toLocaleString()}`, icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50', sub: 'İptaller Hariç' },
          { label: 'Sipariş', value: totalOrders, icon: ShoppingBag, color: 'text-amber-600', bg: 'bg-amber-50', sub: `${completedOrders} Teslim Edildi` },
          { label: 'AOV', value: `₺${avgOrderValue.toFixed(0)}`, icon: TrendingUp, color: 'text-indigo-600', bg: 'bg-indigo-50', sub: 'Ort. Sepet Tutarı' },
          { label: 'İade Oranı', value: `%${cancellationRate.toFixed(1)}`, icon: RotateCcw, color: 'text-red-600', bg: 'bg-red-50', sub: 'İptal Edilenler' },
        ].map((card, i) => (
          <div key={i} className="bg-white p-8 rounded-[2.5rem] border border-stone-100 shadow-sm">
            <div className={`w-12 h-12 ${card.bg} ${card.color} rounded-2xl flex items-center justify-center mb-6`}>
              <card.icon size={24} />
            </div>
            <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1">{card.label}</p>
            <p className="text-3xl font-black text-stone-900">{card.value}</p>
            <p className="text-xs text-stone-300 font-bold mt-4 italic">{card.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white p-10 rounded-[3rem] border border-stone-100 shadow-sm">
          <h3 className="text-xl font-black text-stone-900 mb-10 flex items-center">Satış Trendi</h3>
          <div className="flex items-end justify-between h-64 gap-4 px-4">
            {Object.entries(monthlySales).map(([month, amount]) => (
              <div key={month} className="flex-1 flex flex-col items-center group">
                <div className="w-full bg-amber-100 group-hover:bg-amber-800 transition-all rounded-t-xl" style={{ height: `${(amount / maxMonthly) * 100}%`, minHeight: '4px' }}></div>
                <span className="text-[9px] font-black text-stone-400 uppercase mt-4">{month.slice(0,3)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-10 rounded-[3rem] border border-stone-100 shadow-sm">
          <h3 className="text-xl font-black text-stone-900 mb-8 flex items-center">En Çok Satanlar</h3>
          <div className="space-y-6">
            {bestSellers.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-stone-50 rounded-lg overflow-hidden border border-stone-100">
                    <img src={p.image} className="w-full h-full object-cover" alt={p.name} />
                  </div>
                  <div>
                    <p className="text-xs font-black text-stone-900 line-clamp-1 w-24">{p.name}</p>
                    <p className="text-[9px] text-stone-400 font-bold uppercase">{p.sold} Adet</p>
                  </div>
                </div>
                <p className="text-xs font-black text-amber-800">₺{p.revenue.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;
